import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PropsWithChildren } from "react";

import { CookieConsentBanner } from "@punto/frontend-shared/components/cookie-consent-banner";
import { AnalyticsUserProvider } from "@punto/frontend-shared/providers/analytics-user-provider";
import { ApiAuthProvider } from "@punto/frontend-shared/providers/api-auth-provider";
import { AuthProvider } from "@punto/frontend-shared/providers/auth-provider";
import { CombinedProvider } from "@punto/frontend-shared/providers/combined-provider";
import { CookieConsentProvider } from "@punto/frontend-shared/providers/cookie-consent-provider";
import { HeadProvider } from "@punto/frontend-shared/providers/head-provider";
import { I18nProvider } from "@punto/frontend-shared/providers/i18n-provider";
import { QueryClientProvider } from "@punto/frontend-shared/providers/query-client-provider";
import { FullScreenLoader } from "@punto/ui/components/full-screen-loader";
import { Toaster } from "@punto/ui/components/toaster";
import { TooltipProvider } from "@punto/ui/components/tooltip";

import { AnalyticsPageViews } from "@/lib/analytics/analytics-page-views";
import { AnalyticsProvider } from "@/lib/analytics/analytics-provider";
import { fetchClient } from "@/lib/api";
import { type paths } from "@/lib/api/schema";
import { config } from "@/lib/config";
import { ExtensionOrgSync } from "@/lib/extension/extension-org-sync";
import { AbilityProvider } from "@/modules/abilities";
import { useUser } from "@/modules/auth/hooks/use-user";

const AnalyticsUserProviderWrapper = () => {
  const { user } = useUser();
  return <AnalyticsUserProvider user={user} />;
};

export const RootProvider = ({ children }: PropsWithChildren) => {
  return (
    <CombinedProvider
      providers={[
        <HeadProvider key="head-provider" />,
        <I18nProvider
          key="i18n-provider"
          buildVersion={config.BUILD_VERSION}
        />,
        <AuthProvider key="auth-provider" config={config} />,
        <QueryClientProvider key="query-client-provider" />,
        <ApiAuthProvider<paths>
          key="api-auth-provider"
          fetchClient={fetchClient}
          renderLoading={<FullScreenLoader />}
        />,
        <AbilityProvider key="ability-provider" />,
        <CookieConsentProvider key="cookie-consent-provider" />,
        <AnalyticsProvider key="analytics-provider" />,
      ]}
    >
      <TooltipProvider delayDuration={0}>
        <AnalyticsPageViews />
        <AnalyticsUserProviderWrapper />
        <ExtensionOrgSync />
        {children}
        {import.meta.env.DEV &&
          import.meta.env.VITE_REACT_QUERY_DEVTOOLS !== "false" && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        <Toaster />
        <CookieConsentBanner />
      </TooltipProvider>
    </CombinedProvider>
  );
};
